import {
  ArrowUpRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/16/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Fragment } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook.ts';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context.ts';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context.ts';
import { eIntegrationStatus } from 'src/providers/IntegrationsProvider/Integrations.enums.ts';
import { useUserContext } from 'src/providers/UserProvider/User.context.ts';
import {
  tWapiActivation,
  useWapiNotConnected,
} from 'src/pages/WhatsappApi/hooks/useWapiNotConnected.ts';
import { WAPI_SIDEBAR_ENTRY_STATE } from 'src/pages/WhatsappApi/hooks/useWapiModuleViewAnalytics';
import { useWapiConnectionSignal } from 'src/hooks/whatsapp/useWapiConnectionSignal.hook';
import { eUserRole } from 'src/providers/UserProvider/User.enums.ts';
import { useLayoutContext } from '../../providers/LayoutProvider/Layout.context.ts';
import { useModalsContext } from '../../providers/ModalsProvider/Modals.context.ts';
import { RouterPaths } from '../Router/Router.constants.ts';
import { SidebarAvatar } from './components/Avatar/SidebarAvatar.component.tsx';
import { SidebarConnectionButton } from './components/SidebarConnectionButton.component.tsx';
import { SidebarWorkspaceSelect } from './components/SidebarWorkspaceSelect.component.tsx';
import {
  primaryMenuOptions,
  secondaryMenuOptions,
} from './Sidebar.functions.ts';
import { SIDEBAR_PRIMARY_SECTIONS } from './Sidebar.constants.ts';
import { tSidebarSection } from './Sidebar.types.ts';
import './Sidebar.styles.scss';

const SECTION_LABEL_KEYS: Record<tSidebarSection, string> = {
  crm: 'page.sections.crm',
  tools: 'page.sections.tools',
  channels: 'page.sections.channels',
};

// INT-1516 — the WhatsApp API entry's pill for a held or failed record, in the
// `whatsappApi/whatsappApi` namespace.
const WAPI_ACTIVATION_PILL_KEYS: Record<tWapiActivation, string> = {
  pending: 'page.activation.pending.pill',
  failed: 'page.activation.failed.pill',
};

// Paths where the unsaved-changes guard must fire. Each listed path MUST have
// a guard component mounted that resolves `changesDataModalIsOpen` — see
// docs/solutions/ui-bugs/sidebar-navigation-blocked-by-scoped-leadbot-guard.md.
// The `changesDataModalIsOpen` flag on ModalsProvider is shared between the
// legacy LeadbotUnsavedChangesGuard (on `/leadbot`) and the new
// UnsavedNavigationGuard (on `/leadbot/smart-config`). Route exclusivity
// prevents simultaneous mounting; adding another path here requires mounting
// a corresponding page-level guard or the user will be trapped with no
// resolution UI.
const LEADBOT_CONFIG_PATHS = [
  RouterPaths.leadbot,
  RouterPaths.smartLeadbotConfig,
];

export default function SidebarContent(props: { assistantVisible: boolean }) {
  const { sidebarOpen, toggleSidebar } = useLayoutContext();
  const {
    canUserAccessWhatsAppApi,
    canUserAccessWhatsAppWebQr,
    canUserAccessTikTok,
  } = useACLContext();
  const { isAlertCenterPageEnabled } = useFeatureFlagsContext();
  const location = useLocation();
  const { setSelectedLeadForChatModal, setChangesDataModalIsOpen } =
    useModalsContext();
  const isOnLeadbotPage = LEADBOT_CONFIG_PATHS.includes(location.pathname);
  const { t } = useTranslation('sidebar/sidebarComponent');
  const { t: tAssistant } = useTranslation('assistant/assistant');
  // INT-1516 — the verdict pills live with the rest of the WhatsApp API
  // activation copy, the one namespace shipped in es, en and br (there is no
  // br sidebar namespace). The WhatsApp API provider, mounted app-wide, has
  // already loaded it.
  const { t: tWapi } = useTranslation('whatsappApi/whatsappApi');
  const { currentUserRole } = useUserContext();
  const { whatsappBusinessIntegration, tiktokIntegration } =
    useIntegrationsContext();
  const isConnected =
    whatsappBusinessIntegration.state === eIntegrationStatus.CONNECTED;
  // INT-1492 — with the no-account flag, a workspace with WAPI access sees the
  // module while its account is still connecting, so it can prepare drafts.
  // Same criterion the module itself reads (useWapiNotConnected), which waits
  // for this workspace's flags and integrations to settle. The access check
  // (plan + canSeeWapiIntegration) is unchanged: Legacy and Basic never see it.
  const { isKnownNotConnected: isWapiNotConnectedVisible, activation } =
    useWapiNotConnected();
  const wapiConnectionSignal = useWapiConnectionSignal();
  // INT-1516 — a held or failed record keeps the entry and a pill, but not
  // "Sin conectar": the customer did connect, and the pill names the verdict.
  const wapiPill = !isWapiNotConnectedVisible
    ? undefined
    : activation
      ? tWapi(WAPI_ACTIVATION_PILL_KEYS[activation])
      : t('page.pills.notConnected');

  const sidebarNavClasses = classNames('Sidebar', {
    'Sidebar--open': sidebarOpen,
    'Sidebar--close': !sidebarOpen,
  });

  const sidebarIconClasses = classNames('Sidebar__Option__Icon', {
    'Sidebar__Option__Icon--open': sidebarOpen,
    'Sidebar__Option__Icon--close': !sidebarOpen,
  });

  const SidebarTogglerIcon = sidebarOpen ? ChevronLeftIcon : ChevronRightIcon;

  const sidebarTogglerClasses = classNames('Sidebar__Toggler', {
    'Sidebar__Toggler--open': sidebarOpen,
    'Sidebar__Toggler--close': !sidebarOpen,
  });

  const sidebarTogglerButtonClasses = classNames('Sidebar__Toggler__Button', {
    'Sidebar__Toggler__Button--open': sidebarOpen,
    'Sidebar__Toggler__Button--close': !sidebarOpen,
  });

  const isTiktokConnected =
    tiktokIntegration.state === eIntegrationStatus.CONNECTED;

  const primaryRoutes = primaryMenuOptions(t, {
    canSeeWapiIntegration:
      canUserAccessWhatsAppApi() && (isConnected || isWapiNotConnectedVisible),
    canSeeTiktokIntegration: canUserAccessTikTok() && isTiktokConnected,
    canSeeAlertCenter: isAlertCenterPageEnabled,
    canSeeAssistant: props.assistantVisible,
    assistantLabel: tAssistant('title'),
  });
  const secondaryRoutes = secondaryMenuOptions(t);

  // Group the visible primary options by section: unlabeled first (Dashboard),
  // then each labeled section that still has at least one visible item — a
  // section whose every item is role- or flag-gated away renders nothing, not
  // an orphan heading.
  const visiblePrimaryRoutes = primaryRoutes.filter(
    (option) => option.active && option.userRole.includes(currentUserRole),
  );
  const primarySections = [
    {
      key: null as tSidebarSection | null,
      options: visiblePrimaryRoutes.filter((option) => !option.section),
    },
    ...SIDEBAR_PRIMARY_SECTIONS.map((key) => ({
      key: key as tSidebarSection | null,
      options: visiblePrimaryRoutes.filter((option) => option.section === key),
    })),
  ].filter((group) => group.options.length > 0);

  // Collapsed sidebar has no room for text: the heading degrades to a thin
  // divider so the grouping survives as visual rhythm. role="presentation"
  // because the <li> is not an actionable menu entry — expanded, its label
  // text still reads; collapsed, aria-hidden removes the bare divider.
  function renderSectionHeading(label: string) {
    return (
      <li
        role="presentation"
        aria-hidden={!sidebarOpen}
        className={classNames('Sidebar__Section', {
          'Sidebar__Section--collapsed': !sidebarOpen,
        })}
      >
        <span className="Sidebar__Section__Label">{label}</span>
      </li>
    );
  }

  const shouldShowConnectionButton =
    whatsappBusinessIntegration.state !== eIntegrationStatus.CONNECTED &&
    canUserAccessWhatsAppWebQr();

  return (
    <div className="Sidebar__Parent">
      <nav className={sidebarNavClasses}>
        <menu className="Sidebar__Menu">
          <li className={classNames(sidebarTogglerClasses, 'group/toggler')}>
            <Button
              className={sidebarTogglerButtonClasses}
              animation={false}
              color={'primary'}
              onClick={() => {
                toggleSidebar();
              }}
            >
              <SidebarTogglerIcon className="Sidebar__Toggler__Icon" />
            </Button>
            <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover/toggler:opacity-100">
              {sidebarOpen
                ? t('page.toggler.collapse')
                : t('page.toggler.expand')}
            </span>
          </li>
          {primarySections.map((group) => (
            <Fragment key={group.key ?? 'general'}>
              {group.key !== null &&
                renderSectionHeading(t(SECTION_LABEL_KEYS[group.key]))}
              {group.options.map((option) => {
                const isSeller = currentUserRole === eUserRole.SELLER;
                const href = isSeller
                  ? `${option.href}?showAssigned=true`
                  : option.href;
                const pill =
                  option.href === RouterPaths.wapi ? wapiPill : undefined;

                return (
                  <li key={option.href} className="relative group/nav-item">
                    <Link
                      to={href}
                      // INT-1633 — tells the module's page view it was opened
                      // from the menu.
                      state={
                        option.href === RouterPaths.wapi
                          ? WAPI_SIDEBAR_ENTRY_STATE
                          : undefined
                      }
                      className={classNames('Sidebar__Option', {
                        'btn-active':
                          location.pathname === option.href ||
                          location.pathname.startsWith(option.href + '/'),
                      })}
                      onClick={(e) => {
                        if (
                          isOnLeadbotPage &&
                          localStorage.getItem('hasChangesLeadbot') === 'true'
                        ) {
                          e.preventDefault();
                          setChangesDataModalIsOpen(true);
                          localStorage.setItem(
                            'hrefChangesLeadbot',
                            option.href,
                          );
                        } else {
                          setSelectedLeadForChatModal(null);
                        }
                      }}
                    >
                      {option.href === RouterPaths.wapi &&
                      wapiConnectionSignal ? (
                        // INT-1653 — the dot repeats the disconnection notice:
                        // red while disconnected, blue while Meta moves the
                        // number. Its meaning is in the link's name too.
                        <span className="Sidebar__Option__IconWrap">
                          <option.icon className={sidebarIconClasses} />
                          <span
                            className={classNames(
                              'Sidebar__Option__Dot',
                              `Sidebar__Option__Dot--${wapiConnectionSignal}`,
                            )}
                            data-testid="sidebar-wapi-signal"
                            aria-hidden="true"
                          />
                          <span className="sr-only">
                            {t(`page.wapiSignal.${wapiConnectionSignal}`)}
                          </span>
                        </span>
                      ) : (
                        <option.icon className={sidebarIconClasses} />
                      )}
                      <span
                        className={classNames('Sidebar__Option__Label', {
                          'Sidebar__Option__Label--withPill': pill,
                        })}
                      >
                        {option.label}
                        {pill && (
                          <span className="Sidebar__Option__Pill">{pill}</span>
                        )}
                      </span>
                    </Link>
                    {!sidebarOpen && (
                      <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover/nav-item:opacity-100">
                        {pill ? `${option.label} · ${pill}` : option.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </Fragment>
          ))}
        </menu>

        <menu className="Sidebar__Menu">
          {renderSectionHeading(t('page.sections.workspace'))}
          <li>
            <SidebarWorkspaceSelect />
          </li>
          {shouldShowConnectionButton ? (
            <li>
              <SidebarConnectionButton />
            </li>
          ) : null}

          <li className="relative group/nav-item">
            <a
              target="_blank"
              href="https://help.leadsales.io/es"
              className="Sidebar__Option"
            >
              <QuestionMarkCircleIcon className={sidebarIconClasses} />
              <span className="Sidebar__Option__Label">
                {t('page.routes.help')}
              </span>
              {sidebarOpen && (
                <ArrowUpRightIcon className="ml-auto h-4 w-4 opacity-50 flex-shrink-0" />
              )}
            </a>
            {!sidebarOpen && (
              <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover/nav-item:opacity-100">
                {t('page.routes.help')}
              </span>
            )}
          </li>

          {secondaryRoutes.map((option) => {
            if (!option.userRole.includes(currentUserRole)) {
              return null;
            }
            return (
              <li key={option.href} className="relative group/nav-item">
                <Link
                  to={option.href}
                  className={classNames('Sidebar__Option', {
                    'btn-active':
                      location.pathname === option.href ||
                      location.pathname.startsWith(option.href + '/'),
                  })}
                  onClick={(e) => {
                    if (
                      isOnLeadbotPage &&
                      localStorage.getItem('hasChangesLeadbot') === 'true'
                    ) {
                      e.preventDefault();
                      setChangesDataModalIsOpen(true);
                      localStorage.setItem('hrefChangesLeadbot', option.href);
                    }
                  }}
                >
                  <option.icon className={sidebarIconClasses} />
                  <span className="Sidebar__Option__Label">{option.label}</span>
                </Link>
                {!sidebarOpen && (
                  <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover/nav-item:opacity-100">
                    {option.label}
                  </span>
                )}
              </li>
            );
          })}

          <li>
            <SidebarAvatar />
          </li>
        </menu>
      </nav>
    </div>
  );
}
